import React from "react";
import {
  Box,
  Typography,
  Stack,
  TextField,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Snackbar,
  Alert,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { contentWrite, createPreviewPR } from "../../services/content";
import PublishBar from "../../components/admin/PublishBar";

type CategoryItem = { value: string; key: string };

const CategoriesAdmin: React.FC = () => {
  const [items, setItems] = React.useState<CategoryItem[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [success, setSuccess] = React.useState("");
  const [publishMode, setPublishMode] = React.useState<"prod" | "preview">(
    "prod"
  );
  const [commitMsg, setCommitMsg] = React.useState("chore(categories): update");
  const [branchName, setBranchName] = React.useState("");
  const [prUrl, setPrUrl] = React.useState("");
  const [newValue, setNewValue] = React.useState("");
  const [newKey, setNewKey] = React.useState("");
  const [editIndex, setEditIndex] = React.useState<number | null>(null);
  const [editValue, setEditValue] = React.useState("");
  const [editKey, setEditKey] = React.useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch("/data/categories/index.json", {
        cache: "no-store",
      });
      const json = await res.json();
      setItems(Array.isArray(json.items) ? json.items : []);
    } catch (e: any) {
      setError(e?.message || "Kategoriler okunamadı");
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    load();
  }, []);

  const saveAll = async (next: CategoryItem[]) => {
    setLoading(true);
    setError("");
    try {
      const branch =
        publishMode === "preview"
          ? branchName || `preview/categories-${Date.now()}`
          : undefined;
      await contentWrite(
        "data/categories/index.json",
        JSON.stringify({ items: next }, null, 2),
        `${commitMsg}`,
        branch
      );
      if (branch) {
        const pr = await createPreviewPR(branch, "Categories Update");
        setPrUrl(pr.url);
      }
      setItems(next);
      setSuccess("Kaydedildi");
    } catch (e: any) {
      setError(e?.message || "Kaydedilemedi");
    } finally {
      setLoading(false);
    }
  };

  const addCategory = () => {
    if (!newValue.trim() || !newKey.trim())
      return setError("Değer ve i18n anahtarı gerekli");
    if (!/^products\.filter\.[a-zA-Z0-9]+$/.test(newKey.trim()))
      return setError("i18n anahtarı 'products.filter.xxx' formatında olmalı");
    if (items.some((i) => i.value === newValue.trim()))
      return setError("Aynı değer mevcut");
    if (items.some((i) => i.key === newKey.trim()))
      return setError("Aynı i18n anahtarı mevcut");
    const next = [...items, { value: newValue.trim(), key: newKey.trim() }];
    setItems(next);
    setNewValue("");
    setNewKey("");
  };

  const beginEdit = (idx: number) => {
    setEditIndex(idx);
    setEditValue(items[idx].value);
    setEditKey(items[idx].key);
  };

  const commitEdit = () => {
    if (editIndex === null) return;
    if (!editValue.trim() || !editKey.trim())
      return setError("Değer ve i18n anahtarı gerekli");
    if (!/^products\.filter\.[a-zA-Z0-9]+$/.test(editKey.trim()))
      return setError("i18n anahtarı 'products.filter.xxx' formatında olmalı");
    if (
      items.some((i, idx) => idx !== editIndex && i.value === editValue.trim())
    )
      return setError("Aynı değer mevcut");
    if (items.some((i, idx) => idx !== editIndex && i.key === editKey.trim()))
      return setError("Aynı i18n anahtarı mevcut");
    const next = items.map((it, i) =>
      i === editIndex ? { value: editValue.trim(), key: editKey.trim() } : it
    );
    setItems(next);
    setEditIndex(null);
  };

  const remove = (idx: number) => {
    const next = items.filter((_, i) => i !== idx);
    setItems(next);
  };

  const move = (idx: number, dir: -1 | 1) => {
    const arr = [...items];
    const ni = idx + dir;
    if (ni < 0 || ni >= arr.length) return;
    const tmp = arr[idx];
    arr[idx] = arr[ni];
    arr[ni] = tmp;
    setItems(arr);
  };

  const publish = async () => {
    if (!items.length) {
      // Yayına alınacak kayıt yoksa sessiz geç
    }
    await saveAll(items);
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 3 }}>
        Kategori Yönetimi
      </Typography>

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1}
          sx={{ mb: 2 }}
        >
          <PublishBar
            commitMsg={commitMsg}
            setCommitMsg={setCommitMsg}
            publishMode={publishMode}
            setPublishMode={setPublishMode}
            branchName={branchName}
            setBranchName={setBranchName}
            branchPlaceholder={`preview/categories`}
          />
          <Button
            variant="contained"
            onClick={publish}
            disabled={loading}
          >
            Değişiklikleri Yayına Al
          </Button>
        </Stack>

        <Alert severity="info" sx={{ mb: 2 }}>
          İpucu: Değer kullanıcıya görünen metindir; i18n anahtarı ise çeviri
          dosyalarındaki referanstır (products.filter.xxx). Sürüklemeden önce ok
          tuşlarıyla sıralamayı değiştirin, değişiklikleri yayın çubuğundan
          Prod/PR olarak kaydedin.
        </Alert>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              Yeni Kategori Ekle
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Değer (value)"
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                fullWidth
              />
              <TextField
                label="i18n Anahtarı (key)"
                value={newKey}
                onChange={(e) => setNewKey(e.target.value)}
                fullWidth
              />
              <Button
                startIcon={<AddIcon />}
                variant="contained"
                onClick={addCategory}
                disabled={loading}
              >
                Ekle
              </Button>
            </Stack>
          </CardContent>
        </Card>

        <Grid container spacing={2}>
          {items.map((it, idx) => (
            <Grid item xs={12} key={`${it.value}-${idx}`}>
              <Card>
                <CardContent>
                  {editIndex === idx ? (
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                      <TextField
                        label="Değer (value)"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        fullWidth
                      />
                      <TextField
                        label="i18n Anahtarı (key)"
                        value={editKey}
                        onChange={(e) => setEditKey(e.target.value)}
                        fullWidth
                      />
                      <Button
                        startIcon={<SaveIcon />}
                        variant="contained"
                        onClick={commitEdit}
                        disabled={loading}
                      >
                        Kaydet
                      </Button>
                    </Stack>
                  ) : (
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={2}
                      alignItems="center"
                    >
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 700 }}
                        >
                          {it.value}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {it.key}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1}>
                        <IconButton
                          size="small"
                          onClick={() => move(idx, -1)}
                          disabled={idx === 0}
                        >
                          <ChevronLeftIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => move(idx, 1)}
                          disabled={idx === items.length - 1}
                        >
                          <ChevronRightIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => beginEdit(idx)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => remove(idx)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    </Stack>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Snackbar
          open={!!error}
          onClose={() => setError("")}
          autoHideDuration={5000}
        >
          <Alert severity="error" onClose={() => setError("")}>
            {error}
          </Alert>
        </Snackbar>
        <Snackbar
          open={!!success}
          onClose={() => setSuccess("")}
          autoHideDuration={2500}
        >
          <Alert severity="success" onClose={() => setSuccess("")}>
            {success}
            {prUrl && (
              <Button
                component="a"
                href={prUrl}
                target="_blank"
                size="small"
                sx={{ ml: 1 }}
              >
                PR'ı Aç
              </Button>
            )}
          </Alert>
        </Snackbar>
    </Box>
  );
};

export default CategoriesAdmin;
